import { API_CONFIG } from '../config/api.config';
import { authService } from './auth.service';

export interface ApiError {
  error?: string;
  message: string;
  status: number;
  path?: string;
  timestamp?: string;
  detail?: any[]; // FastAPI validation errors
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    // Get auth headers but exclude Content-Type for FormData
    const authHeaders = authService.getAuthHeaders();
    const isFormData = options.body instanceof FormData;
    
    // Don't set Content-Type for FormData (let browser set it with boundary)
    const baseHeaders = isFormData 
      ? Object.fromEntries(Object.entries(authHeaders).filter(([key]) => key !== 'Content-Type'))
      : authHeaders;
    
    const headers: HeadersInit = {
      ...baseHeaders,
      ...(endpoint.includes('/persons') && /^[1-9]\d*$/.test(localStorage.getItem('secureview_default_location') || '')
        ? { 'X-Workspace-Location': localStorage.getItem('secureview_default_location')! } : {}),
      ...options.headers,
    };

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        authService.clearToken();
        window.location.href = '/login';
        throw new Error('Authentication required');
      }

      // Handle other errors
      if (!response.ok) {
        const fallback: ApiError = { message: `HTTP ${response.status}: ${response.statusText}`, status: response.status };
        const error: ApiError = response.headers.get('content-type')?.includes('application/json')
          ? await response.json().catch(() => fallback)
          : fallback;
        console.error('API Error Response:', error);
        if (error.detail && Array.isArray(error.detail)) {
          console.error('Validation Errors:', JSON.stringify(error.detail, null, 2));
        }
        const enhancedError: any = new Error(error.message || (typeof error.detail === 'string' ? error.detail : 'Request failed'));
        enhancedError.response = { data: error, status: response.status };
        throw enhancedError;
      }

      // Return empty object for 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, any>, options?: Pick<RequestInit, 'signal'>): Promise<T> {
    const queryString = params 
      ? '?' + new URLSearchParams(this.cleanParams(params)).toString()
      : '';
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
      ...options,
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any, options?: Pick<RequestInit, 'signal'>): Promise<T> {
    const isFormData = data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'POST',
      ...options,
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<T> {
    const isFormData = data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * Clean params - remove undefined/null values
   */
  private cleanParams(params: Record<string, any>): Record<string, string> {
    const cleaned: Record<string, string> = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        cleaned[key] = String(params[key]);
      }
    });
    return cleaned;
  }
}

// Export API client instances
export const apiClient = new ApiClient(API_CONFIG.BASE_URL);
export const loginApiClient = new ApiClient(API_CONFIG.LOGIN_URL);
